import { useEffect, useRef } from 'react'

import { EMPTY_OBJECT } from '../../../../constants/common'
import {
  getHasGretaAIChatUnread,
  getIsGretaAIChatChatRunning,
} from '../../GretaAIChat.utils'
import { useGretaAIChatBalance } from '../useGretaAIChatBalance'
import { useGretaAIChatChatActions } from '../useGretaAIChatChatActions'
import { useGretaAIChatChatListLoader } from '../useGretaAIChatChatListLoader'
import { useGretaAIChatConversation } from '../useGretaAIChatConversation'
import { useGretaAIChatDrafts } from '../useGretaAIChatDrafts'
import { useGretaAIChatFeedback } from '../useGretaAIChatFeedback'
import { useGretaAIChatFocus } from '../useGretaAIChatFocus'
import { useGretaAIChatPanelState } from '../useGretaAIChatPanelState'
import { useGretaAIChatRowStatus } from '../useGretaAIChatRowStatus'

import { useGretaAIChatSubmit } from './useGretaAIChatSubmit'

export function useGretaAIChatSession({ displayName } = EMPTY_OBJECT) {
  const launcherRef = useRef(null)
  const panelRef = useRef(null)
  const chatListScrollTopRef = useRef(0)
  const chatListSeenChatIdRef = useRef(null)

  const panelState = useGretaAIChatPanelState()
  const balance = useGretaAIChatBalance()

  const isPanelHiddenRef = useRef(true)
  const activeChatIdRef = useRef(panelState.activeChatId)

  useEffect(() => {
    isPanelHiddenRef.current = !panelState.isOpen || panelState.isMinimized
  }, [panelState.isMinimized, panelState.isOpen])

  useEffect(() => {
    activeChatIdRef.current = panelState.activeChatId
  }, [panelState.activeChatId])

  useEffect(() => {
    balance.checkBalance()
  }, [])

  const chatListLoader = useGretaAIChatChatListLoader({
    activeChatId: panelState.activeChatId,
    isMinimized: panelState.isMinimized,
    isOpen: panelState.isOpen && !balance.isLocked,
    onActiveChatMissing: handleChatMissing,
  })

  const rowStatus = useGretaAIChatRowStatus({ chats: chatListLoader.chats })

  function handleChatCreated({ chatData, chatId, shouldActivate }) {
    if (shouldActivate) {
      panelState.setActiveChatId(chatId)
    }

    chatListLoader.addChat(chatData)
    chatListLoader.refreshChats()
  }

  // Clear only if the probed/loaded chat is still the open one — a slow probe must
  // not drop a selection the user has since moved to another chat.
  function handleChatMissing(chatId) {
    if (chatId === undefined || chatId === activeChatIdRef.current) {
      panelState.setActiveChatId(null)
    }

    if (chatId !== undefined) {
      chatListLoader.dropChat(chatId)
    }
  }

  function handleSelectChat(chatId) {
    rowStatus.clearChatUnread(chatId)
    panelState.setActiveChatId(chatId)
  }

  function handleTurnSettled(chatId, { delivered, isBackground }) {
    rowStatus.markChatSettled(chatId, {
      delivered,
      isBackground: isBackground || isPanelHiddenRef.current,
    })

    // The list was already refreshed when the generation settled.
    if (delivered) {
      balance.checkBalance()
    }
  }

  function handleOpenPanel() {
    panelState.handleOpen()
    rowStatus.clearChatUnread(panelState.activeChatId)
    balance.checkBalance()
  }

  const {
    forgetChat,
    isReplySettling,
    loadOlderMessages,
    messages,
    pendingAnswerFileName,
    reloadMessages,
    retryLastReply,
    sendMessage,
    status,
    stopGeneration,
    trackBackgroundGeneration,
    turnStartedAt,
  } = useGretaAIChatConversation({
    activeChatId: panelState.activeChatId,
    getIsGenerationSettledRecently:
      chatListLoader.getIsChatGenerationSettledRecently,
    onChatCreated: handleChatCreated,
    onChatMissing: handleChatMissing,
    onGenerationSettled: chatListLoader.settleChatGeneration,
    onTurnSettled: handleTurnSettled,
    onTurnStart: rowStatus.markChatGenerating,
  })

  useEffect(() => {
    chatListLoader.chats.forEach(chat => {
      if (getIsGretaAIChatChatRunning(chat)) {
        trackBackgroundGeneration(chat.id)
      }
    })
  }, [chatListLoader.chats, trackBackgroundGeneration])

  const { discardChatDraft, discardDraftIfSubmitted, draft, setDraft } =
    useGretaAIChatDrafts(panelState.activeChatId)

  const chatActions = useGretaAIChatChatActions({
    activeChatId: panelState.activeChatId,
    chatListLoader,
    discardChatDraft,
    forgetChat,
    setActiveChatId: panelState.setActiveChatId,
  })

  const { feedbackByMessageId, submitFeedback } = useGretaAIChatFeedback({
    activeChatId: panelState.activeChatId,
    messages,
  })

  useGretaAIChatFocus({
    isMinimized: panelState.isMinimized,
    isOpen: panelState.isOpen,
    launcherRef,
    panelRef,
  })

  const { isSendPending, submit } = useGretaAIChatSubmit({
    activeChatIdRef,
    balance,
  })

  function handleSendMessage(text) {
    submit(submittedChatId => {
      if (sendMessage(text)) {
        discardDraftIfSubmitted(submittedChatId, text)
      }
    })
  }

  function handleRetry() {
    submit(retryLastReply)
  }

  return {
    actions: {
      setActiveChatId: panelState.setActiveChatId,
      setDraft,
      onChatListResize: panelState.setChatListWidth,
      onCloseChatAction: chatActions.closeChatAction,
      onCloseChatList: panelState.closeChatList,
      onConfirmDeleteChat: chatActions.confirmDeleteChat,
      onConfirmRenameChat: chatActions.confirmRenameChat,
      onDeleteChat: chatActions.openDeleteChat,
      onHoverChatList: panelState.hoverChatList,
      onMinimize: panelState.handleMinimize,
      onNewChat: chatActions.startNewChat,
      onPinChatList: panelState.pinChatList,
      onRenameChat: chatActions.openRenameChat,
      onRetry: handleRetry,
      onRowMenuOpenChange: panelState.setIsChatListOrderFrozen,
      onSelectChat: handleSelectChat,
      onSendMessage: handleSendMessage,
      onStop: stopGeneration,
      onSubmitFeedback: submitFeedback,
      onToggleChatListColumn: panelState.toggleChatListColumn,
      onToggleMode: panelState.handleToggleMode,
      onUnhoverChatList: panelState.unhoverChatList,
    },
    chatListLoader,
    conversation: {
      displayName,
      feedbackByMessageId,
      isSendPending: isSendPending || isReplySettling,
      messages,
      pendingAnswerFileName,
      status,
      turnStartedAt,
      onLoadOlderMessages: loadOlderMessages,
      onReloadMessages: reloadMessages,
    },
    draft,
    launcher: {
      hasUnread: getHasGretaAIChatUnread(
        rowStatus.chatStatusById,
        chatListLoader.chats
      ),
      isLocked: balance.isLocked,
      launcherRef,
      onOpen: handleOpenPanel,
    },
    panel: {
      activeChatId: panelState.activeChatId,
      chatAction: chatActions.chatAction,
      chatListScrollTopRef,
      chatListSeenChatIdRef,
      chatListWidth: panelState.chatListWidth,
      chatStatusById: rowStatus.chatStatusById,
      isChatListColumnOpen: panelState.isChatListColumnOpen,
      isChatListHovered: panelState.isChatListHovered,
      isChatListOrderFrozen: panelState.isChatListOrderFrozen,
      isChatListPinned: panelState.isChatListPinned,
      isLimitReached: balance.isLimitReached,
      isLocked: balance.isLocked,
      isMinimized: panelState.isMinimized,
      isOpen: panelState.isOpen,
      mode: panelState.mode,
      panelRef,
    },
  }
}
